import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Calendar, Star, Award, TrendingUp, ChevronRight, Image as ImageIcon } from 'lucide-react';
import diegoData from '../../data/diegoVitagliano.json';

export const DiegoVitaglianoShowcase: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'highlights' | 'gallery'>('overview');
  const [activeImage, setActiveImage] = useState(0);

  return (
    <section className="py-20 bg-ticmc-dark text-white relative overflow-hidden" id="diego-vitagliano">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute inset-0 bg-[url('portfolio-hotel.jpg')] bg-cover bg-center mix-blend-overlay"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="text-center mb-16">
          <div className="w-32 h-32 mx-auto mb-6 bg-white rounded-full p-2 flex items-center justify-center">
             <img src={diegoData.images.logo} alt={diegoData.profile.name} className="max-w-full max-h-full object-contain" />
          </div>
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-4 text-ticmc-gold">
            {diegoData.profile.name}
          </h2>
          <div className="flex justify-center items-center gap-6 text-gray-300">
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-ticmc-gold" />
              <span>{diegoData.profile.location}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-ticmc-gold" />
              <span>{diegoData.profile.established}</span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Sidebar / Navigation */}
          <div className="lg:col-span-4 space-y-8">
            <div className="bg-white/5 p-8 rounded-sm border border-white/10 backdrop-blur-sm">
              <p className="text-lg leading-relaxed text-gray-300 mb-8 font-serif italic">
                "{diegoData.profile.description}"
              </p>
              
              <div className="space-y-4">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`w-full flex items-center justify-between p-4 rounded-sm transition-all duration-300 ${
                    activeTab === 'overview' 
                      ? 'bg-ticmc-gold text-ticmc-dark font-bold' 
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <span>Success Story</span>
                  <ChevronRight className={`w-5 h-5 transition-transform ${activeTab === 'overview' ? 'rotate-90' : ''}`} />
                </button>
                <button
                  onClick={() => setActiveTab('highlights')}
                  className={`w-full flex items-center justify-between p-4 rounded-sm transition-all duration-300 ${
                    activeTab === 'highlights' 
                      ? 'bg-ticmc-gold text-ticmc-dark font-bold' 
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <span>Key Highlights</span>
                  <ChevronRight className={`w-5 h-5 transition-transform ${activeTab === 'highlights' ? 'rotate-90' : ''}`} />
                </button>
                <button
                  onClick={() => setActiveTab('gallery')}
                  className={`w-full flex items-center justify-between p-4 rounded-sm transition-all duration-300 ${
                    activeTab === 'gallery' 
                      ? 'bg-ticmc-gold text-ticmc-dark font-bold' 
                      : 'bg-white/5 text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <span>Gallery</span>
                  <ChevronRight className={`w-5 h-5 transition-transform ${activeTab === 'gallery' ? 'rotate-90' : ''}`} />
                </button>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-8">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="bg-white/5 p-8 md:p-12 rounded-sm border border-white/10 backdrop-blur-sm min-h-[500px]"
            >
              {activeTab === 'overview' && (
                <div className="space-y-8">
                  <div>
                    <h3 className="text-3xl font-serif font-bold text-white mb-6">{diegoData.successStory.title}</h3>
                    
                    {/* Award Image */}
                    {diegoData.images.award && (
                      <div className="mb-8 rounded-sm overflow-hidden border border-white/10 max-w-md">
                        <img src={diegoData.images.award} alt="Top 50 Pizza Award" className="w-full h-auto" />
                        <div className="bg-ticmc-gold/10 p-2 text-center text-ticmc-gold text-sm font-bold uppercase tracking-wider">
                          Official Recognition
                        </div>
                      </div>
                    )}

                    <div className="space-y-6 text-gray-300">
                      <div>
                        <h4 className="text-ticmc-gold font-bold mb-2 uppercase tracking-wider text-sm">The Challenge</h4>
                        <p>{diegoData.successStory.challenge}</p>
                      </div>
                      <div>
                        <h4 className="text-ticmc-gold font-bold mb-2 uppercase tracking-wider text-sm">The Solution</h4>
                        <p>{diegoData.successStory.solution}</p>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                    {diegoData.successStory.results.map((result, index) => (
                      <div key={index} className="bg-white/5 p-6 rounded-sm border border-white/5 text-center">
                        <div className="mb-4 text-ticmc-gold flex justify-center">
                          {index === 0 ? <Award className="w-8 h-8" /> : 
                           index === 1 ? <TrendingUp className="w-8 h-8" /> : 
                           <Star className="w-8 h-8" />}
                        </div>
                        <p className="font-medium text-white">{result}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {activeTab === 'highlights' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  {diegoData.highlights.map((item, idx) => (
                    <div key={idx} className="group bg-white/5 p-6 rounded-sm hover:bg-white/10 transition-colors">
                      <div className="mb-4 text-ticmc-gold">
                        <Star className="w-6 h-6" />
                      </div>
                      <h4 className="text-xl font-serif font-bold text-white mb-2 group-hover:text-ticmc-gold transition-colors">
                        {item.title}
                      </h4>
                      <p className="text-gray-400 text-sm leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'gallery' && (
                <div className="space-y-8">
                  <h3 className="text-3xl font-serif font-bold text-white mb-6">Atmosphere & Experience</h3>
                  
                  <div className="relative group">
                    <div className="relative aspect-video overflow-hidden rounded-sm border border-white/10 shadow-2xl">
                      <motion.img 
                        key={activeImage}
                        src={diegoData.gallery[activeImage]} 
                        alt="Diego Vitagliano Atmosphere" 
                        initial={{ opacity: 0, scale: 1.05 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.5 }}
                        className="w-full h-full object-cover"
                      />
                      
                      {/* Navigation Dots */}
                      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                        {diegoData.gallery.map((_, idx) => (
                          <button
                            key={idx}
                            onClick={() => setActiveImage(idx)}
                            className={`w-2 h-2 rounded-full transition-all duration-300 ${
                              activeImage === idx ? 'bg-ticmc-gold w-6' : 'bg-white/50 hover:bg-white'
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    {diegoData.gallery.map((img, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImage(idx)}
                        className={`relative aspect-video rounded-sm overflow-hidden border-2 transition-all ${
                          activeImage === idx ? 'border-ticmc-gold opacity-100' : 'border-transparent opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img src={img} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
